// CartDrawer.tsx - Updated to use actual context data
import React, { useEffect, useState } from "react";
import { Drawer, Button, List, Divider } from "antd";
import { useCart } from "./CartContext";
import { useVoucher } from "../../pages/modules/Voucher/VoucherContext";
import { FaTrashAlt } from "react-icons/fa";
import { useProduct } from "../../pages/modules/Product/ProductContext";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";

const CartDrawer: React.FC = () => {
  const { voucherCarts, removeFromCart: removeVoucherFromCart } = useVoucher();
  const { productCarts, removeFromCart: removeProductFromCart } = useProduct();

  const { cartModule, setCartModule, open, hideCart } = useCart();

  const handleCheckout = (module: any, tempId: string) => {
    if (module?.includes("voucher")) {
      window.location.href = `/voucher/checkout/${tempId}`;
    } else {
      window.location.href = `/product/checkout/${tempId}`;
    }
  };

  // Remove entire voucher booking
  const handleRemoveVoucherBooking = (tempId: string) => {
    removeVoucherFromCart(tempId); // Remove entire store
  };

  // Remove entire product booking
  const handleRemoveProductBooking = (tempId: string) => {
    removeProductFromCart(tempId); // Remove entire store
  };

  // Remove specific voucher item
  const handleRemoveVoucherItem = (
    tempId: string,
    voucherId: string,
    priceId: string
  ) => {
    removeVoucherFromCart(tempId, voucherId, priceId);
  };

  // Remove specific product item
  const handleRemoveProductItem = (
    tempId: string,
    productId: string,
    priceId: string,
    pickupDate: string,
    pickupTime: string
  ) => {
    removeProductFromCart(tempId, priceId, pickupDate, pickupTime);
  };

  // Generate unique keys for product items
  const generateProductKey = (product: any, index: number) => {
    return `${product.product_id}-${product.price_id}-${product.pickupDate}-${product.pickupTime}-${index}`;
  };

  // Generate unique keys for voucher items
  const generateVoucherKey = (variant: any, product: any, index: number) => {
    return `${product.voucher_id}-${variant.price_id}-${index}`;
  };

  return (
    // @ts-ignore
    <Drawer title="Cart" placement="right" visible={open} onClose={hideCart}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 20,
        }}
      >
        <Button
          onClick={() => setCartModule("voucher")}
          style={{ width: "100%" }}
          type={cartModule?.includes("voucher") ? "primary" : "default"}
          size={"middle"}
        >
          Voucher
        </Button>
        <Button
          onClick={() => setCartModule("product")}
          style={{ width: "100%" }}
          type={cartModule?.includes("product") ? "primary" : "default"}
          size={"middle"}
        >
          Product
        </Button>
      </div>

      <div style={{ paddingLeft: 20, paddingRight: 20 }}>
        {cartModule?.includes("voucher") ? (
          <VoucherCartSection
            voucherCarts={voucherCarts}
            onRemoveBooking={handleRemoveVoucherBooking}
            onRemoveItem={handleRemoveVoucherItem}
            onCheckout={handleCheckout}
            cartModule={cartModule}
            generateVoucherKey={generateVoucherKey}
          />
        ) : (
          <ProductCartSection
            productCarts={productCarts}
            onRemoveBooking={handleRemoveProductBooking}
            onRemoveItem={handleRemoveProductItem}
            onCheckout={handleCheckout}
            cartModule={cartModule}
            generateProductKey={generateProductKey}
          />
        )}
      </div>
    </Drawer>
  );
};

// Voucher Cart Component
const VoucherCartSection = ({
  voucherCarts,
  onRemoveBooking,
  onRemoveItem,
  onCheckout,
  cartModule,
  generateVoucherKey,
}: any) => (
  <List
    dataSource={voucherCarts || []}
    renderItem={(store: any, storeIndex: number) => (
      <List.Item key={`${store.tempId}-${storeIndex}`}>
        <div style={{ width: "100%" }}>
          {/* Store/Booking Header */}
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {`Booking #${store.tempId}`}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {store.store_name}
          </div>

          {/* Each product in this booking */}
          {store.products.map((product: any, productIndex: number) => (
            <div
              key={`${product.voucher_id}-${productIndex}`}
              style={{ marginBottom: 16 }}
            >
              {/* Product Name */}
              <div style={{ marginBottom: 0 }}>
                <strong>{product.voucher_name}</strong>
              </div>

              {/* Variants List */}
              <div style={{ marginBottom: 8 }}>
                {product.variants.map((variant: any, variantIndex: number) => (
                  <div
                    key={generateVoucherKey(variant, product, variantIndex)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "4px 0",
                      borderBottom:
                        variantIndex === product.variants.length - 1
                          ? "none"
                          : "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div>{variant.title}</div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        RM {parseFloat(variant.amount).toFixed(2)} ×{" "}
                        {variant.quantity}
                      </div>
                    </div>
                    <div style={{ marginLeft: 8 }}>
                      <Button
                        type="link"
                        size="small"
                        danger
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem(
                            store.tempId,
                            product.voucher_id,
                            variant.price_id
                          );
                        }}
                      >
                        <FaTrashAlt />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Product Total */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 8,
                  borderTop: "1px solid #e8e8e8",
                  marginBottom: 12,
                }}
              >
                <div>
                  <strong>Total:</strong>{" "}
                  {product.variants.reduce(
                    (total: number, variant: any) => total + variant.quantity,
                    0
                  )}{" "}
                  item(s)
                </div>
                <div>
                  <strong>
                    RM{" "}
                    {product.variants
                      .reduce(
                        (total: number, variant: any) =>
                          total + parseFloat(variant.amount) * variant.quantity,
                        0
                      )
                      .toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          ))}

          {/* Checkout and Clear Buttons for this booking */}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <Button
              danger
              size="small"
              onClick={() => onRemoveBooking(store.tempId)}
            >
              Remove Booking
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => onCheckout(cartModule, store.tempId)}
            >
              Checkout This Booking
            </Button>
          </div>
        </div>
      </List.Item>
    )}
  />
);

// Product Cart Component
const ProductCartSection = ({
  productCarts,
  onRemoveBooking,
  onRemoveItem,
  onCheckout,
  cartModule,
  generateProductKey,
}: any) => (
  <List
    dataSource={productCarts || []}
    renderItem={(store: any, storeIndex: number) => (
      <List.Item key={`${store.tempId}-${storeIndex}`}>
        <div style={{ width: "100%" }}>
          {/* Store/Booking Header */}
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {`Booking #${store.tempId}`}
          </div>
          <div style={{ fontSize: 16, fontWeight: "bold" }}>
            {store.store_name}
          </div>

          {/* Products List */}
          <div style={{ marginBottom: 0 }}>
            {store.products.map((product: any, productIndex: number) => (
              <div
                key={generateProductKey(product, productIndex)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "8px 0",
                  borderBottom:
                    productIndex === store.products.length - 1
                      ? "none"
                      : "1px solid #f0f0f0",
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* Product Name + Title */}
                  <div style={{ fontWeight: "500" }}>
                    {product.product_name} - {product.title}
                  </div>

                  {/* Price and Quantity */}
                  <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                    {`RM ${parseFloat(product.pricePerItem).toFixed(2)} x ${
                      product.quantity
                    }`}
                  </div>

                  {/* Pickup Date & Time */}
                  {(product.pickupDate || product.pickupTime) && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "#1890ff",
                        marginTop: 4,
                        fontWeight: "500",
                      }}
                    >
                      <CalendarOutlined
                        style={{ marginRight: 4 }}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                      {product.pickupDate && (
                        <span>
                          {moment(product.pickupDate).format("ddd, D MMM")}
                        </span>
                      )}
                      {product.pickupTime && (
                        <span>
                          {product.pickupDate ? " at " : ""}
                          {product.pickupTime}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Variation details */}
                  {product.variations && product.variations.length > 0 && (
                    <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                      {product.variations.map(
                        (variation: any, variationIndex: number) => (
                          <div key={`${variation.optionId}-${variationIndex}`}>
                            {variation.variationTitle}: {variation.optionTitle}
                            {variation.optionPrice > 0 &&
                              ` (+RM ${variation.optionPrice.toFixed(2)})`}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                {/* Item Total and Remove Button */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: "500", fontSize: 14 }}>
                      RM {parseFloat(product.amount).toFixed(2)}
                    </div>
                  </div>
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(
                        store.tempId,
                        product.product_id,
                        product.price_id,
                        product.pickupDate, // Add this
                        product.pickupTime // Add this
                      );
                    }}
                  >
                    <FaTrashAlt />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Store/Booking Summary */}
          <div
            style={{
              padding: "12px 0",
              borderTop: "2px solid #e8e8e8",
              marginTop: 8,
            }}
          >
            {/* Items Count */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 14, color: "#666" }}>Total Items:</div>
              <div style={{ fontSize: 14, fontWeight: "500" }}>
                {store.products.reduce(
                  (total: number, product: any) => total + product.quantity,
                  0
                )}{" "}
                item(s)
              </div>
            </div>

            {/* Booking Total */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 12,
                borderTop: "1px solid #e8e8e8",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: 16 }}>Total:</div>
              <div
                style={{ fontWeight: "bold", fontSize: 16, color: "#e90056" }}
              >
                RM{" "}
                {store.products
                  .reduce(
                    (total: number, product: any) =>
                      total + parseFloat(product.amount) * product.quantity,
                    0
                  )
                  .toFixed(2)}
              </div>
            </div>
          </div>

          {/* Checkout and Clear Buttons */}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              danger
              size="small"
              onClick={() => onRemoveBooking(store.tempId)}
            >
              Remove Booking
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => onCheckout(cartModule, store.tempId)}
            >
              Checkout This Booking
            </Button>
          </div>
        </div>
      </List.Item>
    )}
  />
);

export default CartDrawer;
